import { useEffect, useState } from "react";
import { fetchFeedback, sendFeedback, setFeedbackResolved, type Board, type FeedbackEntry } from "../api.js";
import { formatCT } from "../time.js";

/**
 * Anyone signed in may leave feedback; only a manager or admin may read what
 * came in. The list fetch is only attempted for those roles — an Engineer
 * would get a 403 from the server's own `feedback.view` gate regardless, but
 * asking at all would just be a network round trip to be told no.
 */
export function Feedback({ board }: { board: Board }) {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [entries, setEntries] = useState<FeedbackEntry[] | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  const canView = board.user.role === "manager" || board.user.role === "admin";

  async function toggleResolved(entry: FeedbackEntry) {
    if (resolving) return;
    setResolving(entry.feedbackId);
    try {
      await setFeedbackResolved(entry.feedbackId, !entry.resolvedAt);
      setEntries((await fetchFeedback()).feedback);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setResolving(null);
    }
  }

  useEffect(() => {
    if (!canView) return;
    let live = true;
    fetchFeedback()
      .then((d) => { if (live) setEntries(d.feedback); })
      .catch((e) => { if (live) setErr((e as Error).message); });
    return () => { live = false; };
  }, [canView]);

  async function submit() {
    const text = message.trim();
    if (!text || busy) return;
    setBusy(true); setErr(null); setSent(false);
    try {
      await sendFeedback(text);
      setMessage("");
      setSent(true);
      if (canView) setEntries((await fetchFeedback()).feedback);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <div className="run-sec">
        <h3>Send feedback</h3>
        <p className="hint" style={{ marginTop: 0 }}>
          Anything about the schedule, the tool, or how it's working for you. It goes to your
          managers and admins, not anyone else on your team.
        </p>
        {err && <div className="err" style={{ marginBottom: 10 }}>{err}</div>}
        <textarea
          style={{
            width: "100%", minHeight: 120, resize: "vertical", font: "inherit", fontSize: 13.5,
            padding: "8px 10px", background: "var(--bg)", color: "var(--ink)",
            border: "1px solid var(--rule)",
          }}
          placeholder="What's working, what isn't, what you'd change…"
          value={message}
          onChange={(e) => { setMessage(e.target.value); setSent(false); }}
        />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8 }}>
          <button className="btn on" disabled={!message.trim() || busy} onClick={() => void submit()}>
            {busy ? "Sending…" : "Send feedback"}
          </button>
          {sent && <span className="muted">Sent.</span>}
        </div>
      </div>

      {canView && (
        <div className="run-sec">
          <h3>What people have sent<span className="run-sec-n">{entries?.length ?? "—"}</span></h3>
          {entries === null && !err && <p className="muted">Loading…</p>}
          {entries?.length === 0 && <p className="muted">Nothing yet.</p>}
          {entries?.map((e) => (
            <div
              key={e.feedbackId}
              style={{
                padding: "10px 0", borderTop: "1px solid var(--rule)",
                opacity: e.resolvedAt ? 0.6 : 1,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 10 }}>
                <div className="muted" style={{ fontSize: 12, marginBottom: 4 }}>
                  {e.authorName ?? e.authorEmail ?? "Someone without a name on file"}
                  {" · "}
                  <span className="mono">{formatCT(e.createdAt)}</span>
                  {e.resolvedAt && (
                    <>
                      {" · "}
                      <span>
                        Addressed by {e.resolvedByName ?? "someone"}
                        {" "}
                        <span className="mono">{formatCT(e.resolvedAt)}</span>
                      </span>
                    </>
                  )}
                </div>
                <button
                  className="btn"
                  style={{ flexShrink: 0, fontSize: 12, padding: "2px 8px" }}
                  disabled={resolving === e.feedbackId}
                  onClick={() => void toggleResolved(e)}
                >
                  {resolving === e.feedbackId ? "…" : e.resolvedAt ? "Reopen" : "Mark addressed"}
                </button>
              </div>
              <div style={{ whiteSpace: "pre-wrap" }}>{e.message}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
